import React, { useEffect, useState } from "react";
import { api, formatDate, formatApiErrorDetail } from "../lib/api";
import { Plus, Trash, UserCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const EMPTY = { email: "", password: "", name: "", role: "utente_semplice" };

const ROLES = [
  { v: "super_admin",     label: "Super Admin",     desc: "Accesso totale, incluse impostazioni." },
  { v: "amministratore",  label: "Amministratore",  desc: "Tutto eccetto le impostazioni globali." },
  { v: "consulente",      label: "Consulente",      desc: "Gestisce i propri preventivi, incassi, spese, eventi." },
  { v: "utente_semplice", label: "Utente Semplice", desc: "Sola lettura sui moduli operativi." },
];

const ROLE_LABEL = ROLES.reduce((acc, r) => ({ ...acc, [r.v]: r.label }), {});

const ROLE_CLS = {
  super_admin:     "bg-[#0033A0] text-white border-[#0033A0]",
  amministratore:  "bg-[#0033A0]/10 text-[#0033A0] border-[#0033A0]/30",
  consulente:      "bg-[#F4F5F7] text-[#0A0A0A] border-[#E2E8F0]",
  utente_semplice: "bg-white text-[#64748B] border-[#E2E8F0]",
};

export default function Users() {
  const { user: current } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function save(e) {
    e.preventDefault();
    try {
      await api.post("/users", form);
      toast.success("Utente creato");
      setAdding(false);
      setForm(EMPTY);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore");
    }
  }

  async function remove(u) {
    if (u.id === current?.id) return toast.error("Non puoi eliminare te stesso");
    if (!window.confirm(`Eliminare utente "${u.email}"?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Utente eliminato");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore");
    }
  }

  return (
    <div className="p-4 md:p-12 max-w-[1400px] mx-auto">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 md:mb-10">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-3">Amministrazione</div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-tighter leading-none">Utenti</h1>
          <p className="text-sm text-[#64748B] mt-3">{items.length} account configurati</p>
        </div>
        <button
          onClick={() => setAdding(true)}
          data-testid="new-user-btn"
          className="btn-primary px-5 py-3 text-xs uppercase tracking-[0.15em] font-bold rounded-sm flex items-center gap-2 self-start"
        >
          <Plus size={14} weight="bold" /> Nuovo Utente
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-[#64748B]">Caricamento…</div>
      ) : (
        <div className="swiss-card overflow-hidden">
          {/* Tabella desktop */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F4F5F7]">
                <tr className="text-left">
                  <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Nome</th>
                  <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Email</th>
                  <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Ruolo</th>
                  <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Creato</th>
                  <th className="py-3 px-4"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((u) => (
                  <tr key={u.id} className="border-t border-[#E2E8F0] hover:bg-[#F4F5F7]" data-testid={`user-row-${u.id}`}>
                    <td className="py-3 px-4 font-semibold flex items-center gap-2">
                      <UserCircle size={18} weight="duotone" className="text-[#64748B]" />
                      {u.name}
                      {u.id === current?.id && <span className="text-[9px] uppercase tracking-[0.2em] text-[#0033A0] font-bold">(tu)</span>}
                    </td>
                    <td className="py-3 px-4 text-[#64748B]">{u.email}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-0.5 text-[9px] uppercase tracking-[0.15em] font-bold border rounded-sm ${ROLE_CLS[u.role] || "bg-gray-100 text-gray-800 border-gray-300"}`}>
                        {ROLE_LABEL[u.role] || u.role}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-[#64748B] text-xs">{formatDate(u.created_at)}</td>
                    <td className="py-3 px-4 text-right">
                      {u.id !== current?.id && (
                        <button onClick={() => remove(u)} className="p-1.5 text-[#64748B] hover:text-[#D32F2F]" data-testid={`delete-user-${u.id}`}>
                          <Trash size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Card mobile */}
          <div className="md:hidden divide-y divide-[#E2E8F0]">
            {items.map((u) => (
              <div key={u.id} className="px-4 py-4 flex items-center gap-3" data-testid={`user-card-${u.id}`}>
                <UserCircle size={32} weight="duotone" className="text-[#64748B] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm truncate">{u.name}</span>
                    {u.id === current?.id && <span className="text-[9px] uppercase tracking-[0.2em] text-[#0033A0] font-bold">(tu)</span>}
                  </div>
                  <div className="text-[11px] text-[#64748B] truncate">{u.email}</div>
                  <span className={`mt-1 inline-block px-2 py-0.5 text-[9px] uppercase tracking-[0.15em] font-bold border rounded-sm ${ROLE_CLS[u.role] || "bg-gray-100 text-gray-800 border-gray-300"}`}>
                    {ROLE_LABEL[u.role] || u.role}
                  </span>
                </div>
                {u.id !== current?.id && (
                  <button onClick={() => remove(u)} className="p-2 text-[#64748B] hover:text-[#D32F2F]" data-testid={`delete-user-mobile-${u.id}`}>
                    <Trash size={18} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {adding && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setAdding(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white border border-[#E2E8F0] rounded-sm max-w-md w-full p-6" data-testid="user-modal">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-2">Nuovo</div>
            <h2 className="font-display text-2xl font-bold mb-6">Utente</h2>
            <div className="space-y-4">
              <Field label="Nome *">
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-[#E2E8F0] rounded-sm px-3 py-2 text-sm focus:border-[#0033A0] outline-none" data-testid="user-name-input" />
              </Field>
              <Field label="Email *">
                <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-[#E2E8F0] rounded-sm px-3 py-2 text-sm focus:border-[#0033A0] outline-none" data-testid="user-email-input" />
              </Field>
              <Field label="Password *">
                <input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full border border-[#E2E8F0] rounded-sm px-3 py-2 text-sm focus:border-[#0033A0] outline-none" data-testid="user-password-input" />
              </Field>
              <Field label="Ruolo">
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full border border-[#E2E8F0] rounded-sm px-3 py-2 text-sm focus:border-[#0033A0] outline-none" data-testid="user-role-select">
                  {ROLES.map(r => <option key={r.v} value={r.v}>{r.label}</option>)}
                </select>
                <p className="text-[10px] text-[#64748B] mt-1">{ROLES.find(r => r.v === form.role)?.desc}</p>
              </Field>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button type="button" onClick={() => setAdding(false)} className="px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold border border-[#E2E8F0] rounded-sm hover:bg-[#F4F5F7]">Annulla</button>
              <button type="submit" className="btn-primary px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold rounded-sm" data-testid="user-save-btn">Crea</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] font-bold uppercase tracking-[0.15em] mb-1.5">{label}</label>
      {children}
    </div>
  );
}
